import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Paintbrush, Home as HomeIcon, Building2, Ruler, ArrowRight, CheckCircle2 } from 'lucide-react';
import BeforeAfter from '../components/BeforeAfter';
import { motion } from 'framer-motion';
import { services, testimonials, faqs } from '../data/mock';

export default function HomePage() {
    const featuredServices = [
        {
            icon: <HomeIcon className="h-6 w-6" />,
            title: 'Residential Design',
            description: 'Transform your home into a personalized sanctuary with our bespoke interior solutions.'
        },
        {
            icon: <Building2 className="h-6 w-6" />,
            title: 'Commercial Spaces',
            description: 'Create inspiring work environments that boost productivity and reflect your brand.'
        },
        {
            icon: <Ruler className="h-6 w-6" />,
            title: 'Space Planning',
            description: 'Optimize your layout for maximum functionality and aesthetic appeal.'
        },
        {
            icon: <Paintbrush className="h-6 w-6" />,
            title: 'Custom Furniture',
            description: 'Bespoke furniture pieces designed and crafted to perfectly fit your space.'
        }
    ];

    const featuredImages = [
        'https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2089696/pexels-photo-2089696.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2062426/pexels-photo-2062426.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/3773575/pexels-photo-3773575.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/3773583/pexels-photo-3773583.jpeg?auto=compress&cs=tinysrgb&w=800'
    ];

    return (
        <div data-testid="home-page" className="overflow-x-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center pt-20 pb-16 bg-gradient-to-br from-cream-50 via-white to-brand-light/20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <h2 className="text-brand-primary font-bold text-sm uppercase tracking-[0.2em] mb-4">
                                My Insta Spaces
                            </h2>
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8 text-gray-900">
                                Crafting Elegant Spaces for <span className="text-brand-primary">Modern Living</span>
                            </h1>
                            <p className="text-lg md:text-xl leading-relaxed text-gray-600 mb-10 max-w-xl">
                                Experience the perfect blend of personalised aesthetics and functional design. We transform your vision into timeless interiors that inspire.
                            </p>
                            <div className="flex flex-wrap gap-5">
                                <Link to="/contact">
                                    <Button size="lg" className="bg-brand-primary hover:bg-brand-dark text-white px-8 py-7 text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-brand/20" data-testid="hero-cta-button">
                                        Start Your Project
                                        <ArrowRight className="ml-2 h-5 w-4" />
                                    </Button>
                                </Link>
                                <Link to="/design-ideas">
                                    <Button variant="outline" size="lg" className="border-gray-200 hover:bg-gray-50 px-8 py-7 text-lg rounded-full transition-all duration-300" data-testid="hero-gallery-button">
                                        View Designs
                                    </Button>
                                </Link>
                            </div>

                            <div className="mt-12 flex items-center gap-8 grayscale opacity-70">
                                <div className="flex flex-col">
                                    <span className="text-2xl font-bold text-gray-900">10+</span>
                                    <span className="text-xs uppercase tracking-wider text-gray-500">Years Warranty</span>
                                </div>
                                <div className="w-[1px] h-10 bg-gray-200"></div>
                                <div className="flex flex-col">
                                    <span className="text-2xl font-bold text-gray-900">45</span>
                                    <span className="text-xs uppercase tracking-wider text-gray-500">Day Delivery</span>
                                </div>
                                <div className="w-[1px] h-10 bg-gray-200"></div>
                                <div className="flex flex-col">
                                    <span className="text-2xl font-bold text-gray-900">146</span>
                                    <span className="text-xs uppercase tracking-wider text-gray-500">Quality Checks</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, x: 50 }}
                            animate={{ opacity: 1, scale: 1, x: 0 }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 bg-brand-primary/5 rounded-[2rem] blur-2xl transform rotate-3"></div>
                            <Card className="relative overflow-hidden shadow-2xl rounded-[2rem] border-none">
                                <AspectRatio ratio={4 / 3}>
                                    <img
                                        src="https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                        alt="Modern minimalist living room"
                                        className="w-full h-full object-cover"
                                    />
                                </AspectRatio>
                            </Card>

                            {/* Floating element */}
                            <motion.div
                                animate={{ y: [0, -15, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 z-10 hidden md:block"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900">Quality Guaranteed</h4>
                                        <p className="text-sm text-gray-500">Premium materials only</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                    <div className="text-center mb-16">
                        <span className="text-brand-primary font-bold text-sm uppercase tracking-widest mb-4 block">
                            Our Services
                        </span>
                        <h3 className="text-4xl md:text-5xl font-bold text-gray-900">
                            Comprehensive Interior <span className="text-brand-primary">Solutions</span>
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {featuredServices.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Card className="p-8 rounded-[2rem] border border-gray-100 bg-white hover:border-brand-primary/20 hover:shadow-2xl hover:shadow-brand/5 transition-all duration-500 group" data-testid={`service-card-${index}`}>
                                    <div className="w-14 h-14 bg-brand-light rounded-2xl flex items-center justify-center text-brand-primary mb-6 group-hover:bg-brand-primary group-hover:text-white transition-colors duration-500">
                                        {service.icon}
                                    </div>
                                    <h4 className="text-2xl font-bold mb-3 text-gray-900">{service.title}</h4>
                                    <p className="text-gray-600 leading-relaxed text-base">{service.description}</p>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Portfolio Gallery */}
            <section className="py-24 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                    <div className="text-center mb-16">
                        <span className="text-brand-primary font-bold text-sm uppercase tracking-widest mb-4 block">
                            Portfolio
                        </span>
                        <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            Featured <span className="text-brand-primary">Projects</span>
                        </h3>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Explore our curated collection of residential and commercial spaces that showcase our design philosophy.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-12">
                        {featuredImages.map((image, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                viewport={{ once: true }}
                            >
                                <Card className="overflow-hidden group rounded-3xl border-none shadow-lg hover:shadow-2xl transition-all duration-500" data-testid={`featured-image-${index}`}>
                                    <AspectRatio ratio={4 / 3}>
                                        <img
                                            src={image}
                                            alt={`Interior design project ${index + 1}`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                            <Button variant="outline" className="text-white border-white hover:bg-white hover:text-black rounded-full">Explore</Button>
                                        </div>
                                    </AspectRatio>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center">
                        <Link to="/design-ideas">
                            <Button variant="secondary" size="lg" className="rounded-full px-10 py-7 bg-white border border-gray-200 hover:bg-gray-50 shadow-md" data-testid="view-full-gallery-button">
                                View All Projects
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Before/After Section */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <span className="text-brand-primary font-bold text-sm uppercase tracking-widest mb-4 block">
                                Transformation
                            </span>
                            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                                See the <span className="text-brand-primary">Difference</span>
                            </h3>
                            <p className="text-lg leading-relaxed text-gray-600 mb-8">
                                Every space has potential. Our design approach transforms ordinary rooms into extraordinary environments. Slide to compare the before and after of this stunning residential project.
                            </p>
                            <div className="space-y-4">
                                {[
                                    "Space optimization and layout redesign",
                                    "Custom furniture and lighting solutions",
                                    "Premium materials and finishes",
                                    "146 quality checks for peace of mind"
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-6 h-6 bg-brand-light rounded-full flex items-center justify-center text-brand-primary flex-shrink-0">
                                            <CheckCircle2 className="w-4 h-4" />
                                        </div>
                                        <span className="text-gray-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="absolute -inset-10 bg-brand-primary/5 rounded-full blur-3xl transform -translate-x-10"></div>
                            <BeforeAfter
                                beforeSrc="https://images.pexels.com/photos/5824901/pexels-photo-5824901.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                afterSrc="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 bg-brand-lighter/30 pb-32">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                    <div className="text-center mb-16">
                        <span className="text-brand-primary font-bold text-sm uppercase tracking-widest mb-4 block">
                            Testimonials
                        </span>
                        <h3 className="text-4xl md:text-5xl font-bold text-gray-900">
                            What Our <span className="text-brand-primary">Clients Say</span>
                        </h3>
                    </div>

                    <Carousel className="w-full max-w-5xl mx-auto" data-testid="testimonials-carousel" opts={{ loop: true }}>
                        <CarouselContent className="-ml-4">
                            {testimonials.map((testimonial, index) => (
                                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                                    <Card className="p-8 h-full rounded-[2.5rem] border-none shadow-xl bg-white flex flex-col justify-between">
                                        <div>
                                            <div className="flex gap-1 mb-6 text-brand-primary">
                                                {[1, 2, 3, 4, 5].map(s => <span key={s}>★</span>)}
                                            </div>
                                            <p className="text-lg leading-relaxed mb-8 text-gray-700 italic">
                                                "{testimonial.text}"
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                                                <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900">{testimonial.name}</p>
                                                <p className="text-sm text-gray-500">Happy Homeowner</p>
                                            </div>
                                        </div>
                                    </Card>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <div className="hidden md:block">
                            <CarouselPrevious className="-left-12 bg-white border-gray-100 shadow-md text-brand-primary hover:bg-brand-primary hover:text-white" />
                            <CarouselNext className="-right-12 bg-white border-gray-100 shadow-md text-brand-primary hover:bg-brand-primary hover:text-white" />
                        </div>
                    </Carousel>
                </div>
            </section>

        </div>
    );
}
